import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { LibModule } from '@usitsdasdesign/dds-ng';
import { filter, finalize, forkJoin, Observable, of, shareReplay, switchMap, takeUntil } from 'rxjs';
import { ECategoriesThemes, ICategory, ICategoryThemeSetting } from '../../shared/categories/typings';
import { ArticleService } from './article.service';
import { Destroy } from '../../core/destroyClass';
import { CategoriesService } from '../../shared/categories/categories.service';
import { ISubCategoryDetails } from './typings';
import { CATEGORIES_THEMES_SETTINGS } from '../../shared/categories/constants';
import { ArticleNavigationComponent } from './article-navigation/article-navigation.component';
import { SpinnerService } from '../../shared/spinner/spinner.service';
import { SafeHtml } from '@angular/platform-browser';
import { HtmlSanitizerService } from '../../shared/services/html-sanitizer.service';
import { hasRealContent } from '../../shared/utils/content-utils';

const POLICIES_MAX_COUNT_DEFAULT = 4;
const PAGE_CLASS_NAME = 'article-page';

@Component({
  selector: 'app-article',
  standalone: true,
  imports: [
    CommonModule,
    LibModule,
    ArticleNavigationComponent,
  ],
  providers: [ArticleService],
  templateUrl: './article.component.html',
  styleUrl: './article.component.scss',
})
export class ArticleComponent extends Destroy implements AfterViewChecked {
  selectedCategory: ICategory;
  themeSettings: ICategoryThemeSetting;
  articleData: ISubCategoryDetails;
  articleDataObservable$: Observable<ISubCategoryDetails>;
  codeId: string;

  policiesMaxCount = POLICIES_MAX_COUNT_DEFAULT;
  POLICIES_MAX_COUNT_DEFAULT = POLICIES_MAX_COUNT_DEFAULT;
  policiesExpanded: boolean;
  @ViewChild('relatedPoliciesContent') relatedPoliciesContent!: ElementRef;
  isContentOverflowing = false;
  private contentOverflowChecked = false;
  
  safeSubCategoryContent: SafeHtml;
  safeGuidanceContent: SafeHtml;
  safeRelatedPoliciesContent: SafeHtml;
  safeQaContents: SafeHtml[] = [];
  hasRealContent = hasRealContent;

  constructor(
    private activateRoute: ActivatedRoute,
    private articleService: ArticleService,
    private categoriesService: CategoriesService,
    private renderer: Renderer2,
    private router: Router,
    private spinnerService: SpinnerService,
    private cdr: ChangeDetectorRef,
    private htmlSanitizer: HtmlSanitizerService
  ) {
    super();
  }

  ngOnInit() {
    this.renderer.addClass(document.body, PAGE_CLASS_NAME);
    this.initArticle();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.initArticle();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.renderer.removeClass(document.body, PAGE_CLASS_NAME);
  }

  private initArticle(): void {
    this.spinnerService.show();
    this.codeId = this.activateRoute.snapshot.params['codeId'];
    const categoriesObservable = this.ensureCategoriesLoaded();
    this.articleDataObservable$ = categoriesObservable.pipe(
      switchMap(() => this.getArticleData(this.codeId)),
      shareReplay(1),
      finalize(() => {
        this.resetFlags();
        this.spinnerService.hide();
      })
    );

    this.articleDataObservable$.subscribe({
      next: (data) => {
        const sanitizedContent = this.htmlSanitizer.sanitizeAppContent(data);
        this.safeSubCategoryContent = sanitizedContent.safeSubCategoryContent;
        this.safeGuidanceContent = sanitizedContent.safeGuidanceContent;
        this.safeRelatedPoliciesContent = sanitizedContent.safeRelatedPoliciesContent;
        this.safeQaContents = sanitizedContent.safeQaContents;
        this.themeSettings = this.getThemeSettings();
      },
      error: (error) => {
        this.spinnerService.hide();
        this.router.navigate(['/']);
      }
    });
  }

  private ensureCategoriesLoaded(): Observable<ICategory[]> {
    if (this.categoriesService.selectedCategory) {
      this.selectedCategory = this.categoriesService.selectedCategory;
      return of([this.selectedCategory]);
    }
    return this.categoriesService.getCategoriesData().pipe(
      switchMap(categories => {
        return this.categoriesService.getSubCategoriesData().pipe(
          switchMap(subCategories => {
            const formattedCategories = this.processCategories(categories, subCategories);
            let foundCategory = null;
            for (const category of formattedCategories) {
              const matchingSubCategory = category.list.find(sub => sub.codeId == this.codeId);
              if (matchingSubCategory) {
                foundCategory = category;
                break;
              }
            }
            
            if (foundCategory) {
              this.selectedCategory = foundCategory;
              this.categoriesService.selectedCategory = this.selectedCategory;
            } else {
              this.selectedCategory = formattedCategories[0] || {
                theme: ECategoriesThemes.blue,
                path: '',
                id: '',
                title: '',
                list: [],
                order: 0
              };
            }
            return of(formattedCategories);
          })
        );
      })
    );
  }

  private processCategories(categories: any[], subCategories: any[]): ICategory[] {
    const sortedCategories = categories.sort((a, b) => a.order - b.order);
    return sortedCategories.map((category) => {
      const relatedSubcategories = subCategories
        .filter((sub) => sub.categoryId == category.id)
        .sort((a, b) => a.order - b.order)
        .map((sub) => ({
          id: sub.id,
          title: sub.subCategoryName,
          description: sub.subCategoryDescription,
          path: this.generatePath(sub.subCategoryName),
          order: sub.order,
          codeId: sub.codeId,
        }));
      
      return {
        id: category.id,
        title: category.categoryName,
        path: this.generatePath(category.categoryName),
        theme: category.theme,
        list: relatedSubcategories,
        order: category.order,
      } as ICategory;
    });
  }

  private generatePath(title: string): string {
    return title.toLowerCase()
      .trim()
      .replace(/[\s]+/g, '-')
      .replace(/[^\w\-]+/g, '');
  }

  private getArticleData(codeId: any): Observable<ISubCategoryDetails> {
    return this.articleService.getSubCategoryDetails(codeId);
  }

  private getThemeSettings(): ICategoryThemeSetting {
    const selectedTheme = this.selectedCategory?.theme || ECategoriesThemes.blue;

    return CATEGORIES_THEMES_SETTINGS.find(theme => theme.name === selectedTheme)!!;
  }

  togglePolicies(): void {
    this.policiesExpanded = !this.policiesExpanded;
  }
  ngAfterViewChecked(): void {
    if (!this.contentOverflowChecked && this.relatedPoliciesContent) {
      this.checkContentOverflow();
      this.contentOverflowChecked = true;
    }
  }
  checkContentOverflow(): void {
    const element = this.relatedPoliciesContent?.nativeElement;
    if (element) {
      this.isContentOverflowing = element.scrollHeight > element.clientHeight;
      this.cdr.detectChanges();
    }
  }

  private resetFlags(): void {
    this.policiesExpanded = false;
    this.contentOverflowChecked = false;
    this.isContentOverflowing = false;
  }

  hasQaContent(qaItem: any): boolean {
    return qaItem && (!!qaItem.title || this.hasRealContent(qaItem.content));
  }

  hasAnyQaContent(qaItems: any[]): boolean {
    if (!qaItems) return false;
    return qaItems.some(item => this.hasQaContent(item));
  }
}
